import React, { startTransition, useEffect, useRef, useState, useCallback } from "react";
import { CommandIcon, EyeIcon, EyeOffIcon } from "lucide-react";

import {
  peekPairingTokenFromUrl,
  resolvePrimaryEnvironmentHttpUrl,
  submitServerCommandAccessToken,
  submitServerCmmdPasswordLogin,
  stripPairingTokenFromCurrentUrl,
  submitServerAuthCredential,
} from "../../environments/primary";
import {
  pollCommandAccessToken,
  requestCommandDeviceAuthorization,
} from "../../environments/primary/cmmdConnectionClient";
import { captureWebError } from "../../sentry";
import { isElectron } from "../../env";
import { resolveCmmdControlPlaneHttpUrl } from "../../lib/desktopCloudAuth";
import { Button } from "../ui/button";
import { PairingRouteHeading } from "./PairingRouteHeading";
import {
  errorMessageFromUnknown,
  formatAuthErrorMessage,
  reloadIntoAuthenticatedApp,
  resolveInitialAuthMessage,
  startDesktopCmmdLoginIfAvailable,
  startDesktopCmmdPasswordLoginIfAvailable,
} from "./PairingRouteSurface.logic";

type CommandAuthStatus =
  | {
      readonly phase: "idle";
    }
  | {
      readonly phase: "starting";
    }
  | {
      readonly phase: "awaiting";
      readonly userCode: string;
      readonly verificationUrl: string;
    };

function resolveCmmdForgotPasswordHref(): string {
  try {
    return `${resolveCmmdControlPlaneHttpUrl()}/forgot-password`;
  } catch {
    return "https://cmmd.ai/forgot-password";
  }
}

export function PairingPendingSurface() {
  return (
    <div className="relative flex min-h-full flex-1 items-start justify-center overflow-hidden bg-background px-4 pt-[clamp(4rem,14svh,6.5rem)] pb-8 text-foreground sm:items-center sm:px-6 sm:py-10">
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute inset-0 bg-[linear-gradient(160deg,color-mix(in_srgb,var(--background)_92%,var(--color-black))_0%,var(--background)_56%)]" />
      </div>

      <section className="relative w-full max-w-md rounded-2xl border border-border/80 bg-card/90 p-5 shadow-sm backdrop-blur-md sm:rounded-xl sm:p-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Preparing login
          </h1>
          <p className="mt-3 max-w-prose text-base leading-7 text-muted-foreground sm:text-sm sm:leading-relaxed">
            Finishing a secure connection to your Forge server.
          </p>
        </div>
      </section>
    </div>
  );
}

export function PairingRouteSurface({
  initialErrorMessage,
  onAuthenticated,
}: {
  initialErrorMessage?: string;
  onAuthenticated: () => void;
}) {
  const autoPairTokenRef = useRef<string | null>(peekPairingTokenFromUrl());
  const [errorMessage, setErrorMessage] = useState(
    formatAuthErrorMessage(resolveInitialAuthMessage(initialErrorMessage)),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [commandAuthStatus, setCommandAuthStatus] = useState<CommandAuthStatus>({ phase: "idle" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const autoSubmitAttemptedRef = useRef(false);
  const commandAuthAbortRef = useRef<AbortController | null>(null);

  const submitCredential = useCallback(
    async (nextCredential: string) => {
      setIsSubmitting(true);
      setErrorMessage("");

      const submitError = await submitServerAuthCredential(nextCredential).then(
        () => null,
        (error) => errorMessageFromUnknown(error),
      );

      setIsSubmitting(false);

      if (submitError) {
        setErrorMessage(submitError);
        return;
      }

      startTransition(() => {
        onAuthenticated();
      });
    },
    [onAuthenticated],
  );

  useEffect(() => {
    const token = autoPairTokenRef.current;
    if (!token || autoSubmitAttemptedRef.current) {
      return;
    }

    autoSubmitAttemptedRef.current = true;
    stripPairingTokenFromCurrentUrl();
    void submitCredential(token);
  }, [submitCredential]);

  useEffect(
    () => () => {
      commandAuthAbortRef.current?.abort();
      commandAuthAbortRef.current = null;
    },
    [],
  );

  const handleCommandLogin = useCallback(async () => {
    commandAuthAbortRef.current?.abort();
    const abortController = new AbortController();
    commandAuthAbortRef.current = abortController;
    setCommandAuthStatus({ phase: "starting" });
    setErrorMessage("");

    try {
      if (await startDesktopCmmdLoginIfAvailable()) {
        return;
      }

      const deviceAuthorization = await requestCommandDeviceAuthorization(abortController.signal);
      setCommandAuthStatus({
        phase: "awaiting",
        userCode: deviceAuthorization.user_code,
        verificationUrl: deviceAuthorization.verification_uri_complete,
      });

      window.open(
        deviceAuthorization.verification_uri_complete,
        "forge-command-oauth",
        "popup=true,width=540,height=720",
      );

      const tokenResult = await pollCommandAccessToken(deviceAuthorization, abortController.signal);
      setCommandAuthStatus({ phase: "idle" });
      await submitServerCommandAccessToken(tokenResult.access_token, tokenResult.user, {
        refreshToken: tokenResult.refresh_token,
        mcpToken: tokenResult.mcp_token,
      });
      startTransition(() => {
        onAuthenticated();
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }
      captureWebError(error, { route: "pairing-command-login" });
      setErrorMessage(errorMessageFromUnknown(error));
    } finally {
      setCommandAuthStatus({ phase: "idle" });
      if (commandAuthAbortRef.current === abortController) {
        commandAuthAbortRef.current = null;
      }
    }
  }, [onAuthenticated]);

  const handleCancelCommandLogin = useCallback(() => {
    commandAuthAbortRef.current?.abort();
    commandAuthAbortRef.current = null;
    setCommandAuthStatus({ phase: "idle" });
  }, []);

  const isCommandLoginPending = commandAuthStatus.phase !== "idle";
  const isBusy = isSubmitting || isCommandLoginPending || isEmailSubmitting;

  const handleEmailLogin = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isBusy) {
        return;
      }
      setIsEmailSubmitting(true);
      setErrorMessage("");

      try {
        const formData = new FormData(event.currentTarget);
        const nextEmail = String(formData.get("email") ?? "").trim();
        const nextPassword = String(formData.get("password") ?? "");
        if (
          await startDesktopCmmdPasswordLoginIfAvailable({
            email: nextEmail,
            password: nextPassword,
          })
        ) {
          return;
        }
        const { serverEstablishedSession } = await submitServerCmmdPasswordLogin({
          email: nextEmail,
          password: nextPassword,
        });
        if (serverEstablishedSession) {
          reloadIntoAuthenticatedApp();
          return;
        }
        startTransition(() => {
          onAuthenticated();
        });
      } catch (error) {
        captureWebError(error, { route: "pairing-cmmd-password-login" });
        setErrorMessage(errorMessageFromUnknown(error));
      } finally {
        setIsEmailSubmitting(false);
      }
    },
    [isBusy, onAuthenticated],
  );

  const commandButtonLabel =
    commandAuthStatus.phase === "starting"
      ? "Opening CMMD..."
      : commandAuthStatus.phase === "awaiting"
        ? "Waiting for approval..."
        : "Continue with CMMD";
  const cmmdForgotPasswordHref = resolveCmmdForgotPasswordHref();

  return (
    <div className="relative flex min-h-full flex-1 items-start justify-center overflow-hidden bg-background px-4 pt-[clamp(4rem,14svh,6.5rem)] pb-8 text-foreground sm:items-center sm:px-6 sm:py-10">
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute inset-0 bg-[linear-gradient(160deg,color-mix(in_srgb,var(--background)_92%,var(--color-black))_0%,var(--background)_56%)]" />
      </div>

      <section className="relative w-full max-w-lg rounded-2xl border border-border/80 bg-card/90 p-5 shadow-sm backdrop-blur-md sm:rounded-xl sm:p-8">
        <div>
          <PairingRouteHeading />

          <div className="mt-6">
            {isElectron ? (
              <>
                <Button
                  className="min-h-touch w-full justify-center gap-3 text-base font-semibold sm:text-sm"
                  disabled={isBusy}
                  onClick={() => void handleCommandLogin()}
                  type="button"
                  variant="white"
                >
                  <CommandIcon aria-hidden className="size-4.5 shrink-0 opacity-95" />
                  <span>{commandButtonLabel}</span>
                </Button>

                {commandAuthStatus.phase === "awaiting" ? (
                  <div className="mt-4 rounded-lg border border-border/70 bg-background/55 px-3 py-3 text-xs text-muted-foreground">
                    <p className="text-[11px] font-medium tracking-[0.12em] uppercase">
                      Verification code
                    </p>
                    <p className="mt-1 text-sm text-foreground">
                      <span className="font-mono tabular-nums">{commandAuthStatus.userCode}</span>
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        onClick={() =>
                          window.open(commandAuthStatus.verificationUrl, "_blank", "noreferrer")
                        }
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        Open link
                      </Button>
                      <Button
                        onClick={handleCancelCommandLogin}
                        size="sm"
                        type="button"
                        variant="ghost"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <Button
                className="min-h-touch w-full justify-center gap-3 text-base font-semibold sm:text-sm"
                disabled={isBusy}
                onClick={() => {
                  window.location.href = resolvePrimaryEnvironmentHttpUrl("/api/auth/login");
                }}
                type="button"
                variant="white"
              >
                <CommandIcon aria-hidden className="size-4.5 shrink-0 opacity-95" />
                <span>Continue with CMMD</span>
              </Button>
            )}
          </div>

          {errorMessage ? (
            <div className="mt-4 rounded-lg border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive">
              <p className="whitespace-pre-line">{errorMessage}</p>
            </div>
          ) : null}

          <div className="my-6 flex items-center gap-3 text-[11px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
            <div className="h-px flex-1 bg-border/70" />
            <span>or log in with email</span>
            <div className="h-px flex-1 bg-border/70" />
          </div>

          <form className="space-y-4" onSubmit={(event) => void handleEmailLogin(event)}>
            <label className="block text-sm font-medium text-foreground" htmlFor="cmmd-email">
              Email
            </label>
            <input
              autoComplete="email"
              className="min-h-touch w-full rounded-lg border border-input bg-background/70 px-3 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:ring-2 focus:ring-primary/30 sm:text-sm"
              disabled={isBusy}
              id="cmmd-email"
              name="email"
              onChange={(event) => setEmail(event.currentTarget.value)}
              placeholder="you@cmmd.ai"
              type="email"
              value={email}
            />

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm font-medium text-foreground" htmlFor="cmmd-password">
                  Password
                </label>
                <a
                  className="inline-flex min-h-touch items-center text-sm font-medium text-primary underline-offset-4 hover:underline"
                  href={cmmdForgotPasswordHref}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <input
                  autoComplete="current-password"
                  className="min-h-touch w-full rounded-lg border border-input bg-background/70 px-3 pr-12 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:ring-2 focus:ring-primary/30 sm:text-sm"
                  disabled={isBusy}
                  id="cmmd-password"
                  name="password"
                  onChange={(event) => setPassword(event.currentTarget.value)}
                  placeholder="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                />
                <button
                  aria-label={showPassword ? "Hide typed characters" : "Reveal typed characters"}
                  className="absolute top-1/2 right-0 inline-flex size-touch -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                  disabled={isBusy}
                  onClick={() => setShowPassword((value) => !value)}
                  type="button"
                >
                  {showPassword ? (
                    <EyeOffIcon aria-hidden className="size-4" />
                  ) : (
                    <EyeIcon aria-hidden className="size-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              className="min-h-touch w-full justify-center border-primary bg-primary text-base font-semibold text-primary-foreground shadow-primary/24 hover:bg-primary/90 sm:text-sm"
              disabled={isBusy}
              type="submit"
            >
              {isEmailSubmitting ? "Checking credentials..." : "Continue"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
